/**
 * layout - the contract detail's binding of the shared detail-page surface.
 *
 * A contract's detail page is the same pattern as a matter's: one CANVAS in the
 * middle (the tab bar + the active tab) with glass panes floating over its
 * edges. Two panes:
 *
 *   context   the contract's facts at a glance + renewal + parties  (left)
 *   aiUsage   what this contract has spent on AI intelligence        (right)
 *
 * Page tabs (the canvas's substance): Overview, Obligations, Risk.
 *
 * Entity-scoped so each contract remembers its own pane arrangement.
 */

import {
  DockSide,
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';
import { isAiUsageEnabled } from '../../../config/featureFlags';

export type { DockSide };

export type ContractPanelId = 'context' | 'aiUsage';

export type ContractTab = 'Overview' | 'Obligations' | 'Risk';

export const CONTRACT_TABS: ContractTab[] = ['Overview', 'Obligations', 'Risk'];

export type ContractPanelSlot = GenericSlot<ContractPanelId>;
export type ContractPanelLayout = GenericLayout<ContractPanelId>;

/* AI usage is dropped from the surface where the feature is not released. */
export const CONTRACT_PANEL_IDS: ContractPanelId[] = [
  'context',
  ...(isAiUsageEnabled() ? (['aiUsage'] as ContractPanelId[]) : []),
];

const CONTRACT_PANES: Record<ContractPanelId, DetailPaneConfig> = {
  // Context is open by default: the lifecycle facts are what the tabs are read
  // against.
  context: {
    label: 'Context',
    bounds: { min: 240, max: 640 },
    dock: 'left', index: 0, width: 300, visible: true, rail: true,
  },
  // What this contract has spent on AI intelligence - a pane, not a tab, exactly
  // like the matter surface.
  aiUsage: {
    label: 'AI usage',
    bounds: { min: 300, max: 640 },
    dock: 'right', index: 0, width: 360, visible: false, rail: true,
  },
};

export const CONTRACT_SURFACE = defineDetailSurface<ContractPanelId, ContractTab>({
  kind: 'detail',
  name: 'contract',
  scope: 'entity',
  ids: CONTRACT_PANEL_IDS,
  panes: CONTRACT_PANES,
  tabs: CONTRACT_TABS,
});

export const CONTRACT_PANEL_LABELS: Record<ContractPanelId, string> = CONTRACT_SURFACE.labels;
export const CONTRACT_PANEL_BOUNDS: Record<ContractPanelId, PanelBounds> = CONTRACT_SURFACE.bounds;
export const CONTRACT_DEFAULT_LAYOUT: ContractPanelLayout = CONTRACT_SURFACE.defaults;
